import { useState } from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

type TopTabKey = "all" | "lab" | "treatments" | "monitorings" | "opened-cards" | "processes";

type TopTab = {
  key: TopTabKey;
  label: string;
  iconSrc: string;
  iconWidth?: number;
  iconHeight?: number;
};

type TopBarProps = {
  onTabChange?: (tab: TopTabKey) => void;
};

const tabs: TopTab[] = [
  { key: "all", label: "Tümü", iconSrc: "/assets/icons/patient-table/all.svg" },
  { key: "lab", label: "Laboratuvar", iconSrc: "/assets/icons/patient-table/lab.svg" },
  { key: "treatments", label: "Tedaviler", iconSrc: "/assets/icons/patient-table/order.svg" },
  { key: "monitorings", label: "İzlemler", iconSrc: "/assets/icons/patient-table/viewers.svg" },
  { key: "opened-cards", label: "Açılan Kartlar", iconSrc: "/assets/icons/patient-table/opened-cards.svg" },
  { key: "processes", label: "İşlemler", iconSrc: "/assets/icons/patient-table/processes.svg", iconWidth: 24 },
];

export default function TopBar({ onTabChange }: Readonly<TopBarProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeKey, setActiveKey] = useState<TopTabKey>("all");

  const handleTabClick = (key: TopTabKey) => {
    setActiveKey(key);
    onTabChange?.(key);
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: "12px", sm: "16px" },
        py: "13px",
        background: "#fff",
        overflow: "hidden",
        borderRadius: "10px",
        borderBottom: "1px solid #DBE2EB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        direction="row"
        alignItems="stretch"
        justifyContent="center"
        useFlexGap
        sx={{ width: "100%", flexWrap: "wrap", overflowX: "hidden", gap: { xs: 1, sm: 0 } }}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;

          return (
            <Box
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              sx={{
                px: { xs: "10px", sm: "12px" },
                py: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                cursor: "pointer",
                borderBottom: isActive ? "2px solid #2979C3" : "2px solid transparent",
                borderRadius: { xs: "8px", sm: 0 },
                background: { xs: isActive ? "rgba(41, 121, 195, 0.08)" : "#F8FAFC", sm: "transparent" },
                color: isActive ? "#2979C3" : "#B2BAC4",
                flexShrink: 0,
                flex: isMobile ? "1 1 calc(50% - 8px)" : "0 0 auto",
                minWidth: 0,
              }}
            >
              <Box sx={{ width: tab.iconWidth ?? 20, height: tab.iconHeight ?? 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box
                  component="img"
                  src={tab.iconSrc}
                  alt={tab.label}
                  sx={{
                    width: tab.iconWidth ?? 20,
                    height: tab.iconHeight ?? 20,
                    objectFit: "contain",
                    filter: isActive ? "none" : "grayscale(100%) brightness(0.78)",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  color: isActive ? "#2979C3" : "#B2BAC4",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: isActive ? 700 : 400,
                  wordWrap: "break-word",
                  lineHeight: 1.2,
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
